import { useEffect, useMemo, useState } from "react";
import {
  LISTING_FORMATS,
  LISTING_FORMAT_LABELS,
  STATUS_LABELS,
  buildListing,
  photoPath,
  thumbPath,
  withheldCount,
  type Item,
  type ItemStatus,
  type ListingFormat,
} from "@iltocat/shared";
import { ApiError, api, fetchPhoto, plural } from "../lib/api.js";
import { downloadBlob } from "../lib/download.js";
import { zipStore } from "../lib/zip.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { Segmented } from "@vcnet/ui";
import { useToast } from "../components/Toast.js";

/**
 * From an item to something postable: the text of a listing in a marketplace's
 * own vocabulary, each part with its own copy button, the photos to upload, and
 * the status change that says the thing is now for sale. The text is the main
 * column; photos and status sit beside it on a desktop and below it on a phone.
 *
 * Everything on the page is built by `buildListing()` in `@iltocat/shared`,
 * which reads public fields only; this screen never touches `item.notes` or a
 * private field, and says how many it left out. Nothing here is saved except
 * the status, and only when a person presses the button.
 *
 * The chosen format lives in `localStorage` as a convenience, not in the
 * address: it is a habit ("I only ever post to the same place"), not a place.
 */

const FORMAT_KEY = "iltocat.listing-format";

const savedFormat = (): ListingFormat => {
  try {
    const value = window.localStorage.getItem(FORMAT_KEY);
    return LISTING_FORMATS.find((format) => format === value) ?? "social";
  } catch {
    return "social";
  }
};

/** A file name a person can live with: the title, lowercased, with runs of anything odd made a hyphen. */
const slug = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "item";

export function Listing({
  id,
  taxonomy,
  onItem,
}: {
  id: string;
  taxonomy: Taxonomy;
  onItem: (id: string) => void;
}) {
  const [item, setItem] = useState<Item | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">("loading");
  const [format, setFormat] = useState<ListingFormat>(savedFormat);
  const [copyProblem, setCopyProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let live = true;
    void api.items
      .get(id)
      .then(({ item: found }) => {
        if (!live) return;
        setItem(found);
        setState("ready");
      })
      .catch(() => {
        if (live) setState("missing");
      });
    return () => {
      live = false;
    };
  }, [id]);

  const categoryName = item
    ? (taxonomy.categories.find((category) => category.id === item.categoryId)?.name ?? null)
    : null;

  const listing = useMemo(
    () => (item ? buildListing({ item, categoryName }, format) : null),
    [item, categoryName, format],
  );

  function choose(next: ListingFormat) {
    setFormat(next);
    setCopyProblem(null);
    try {
      window.localStorage.setItem(FORMAT_KEY, next);
    } catch {
      // A habit that is not remembered is only a habit that is not remembered.
    }
  }

  async function copy(label: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyProblem(null);
      toast(`${label} copied.`);
    } catch {
      setCopyProblem("Your browser would not copy for us. Select the text and press Ctrl+C or ⌘C.");
    }
  }

  async function downloadAll() {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      const files = await Promise.all(
        item.photos.map(async (photo, index) => ({
          name: `${slug(item.title)}-${index + 1}.jpg`,
          data: await fetchPhoto(photo.id),
        })),
      );
      downloadBlob(
        new Blob([zipStore(files)], { type: "application/zip" }),
        `${slug(item.title)}-photos.zip`,
      );
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "The photos did not download.");
    } finally {
      setBusy(false);
    }
  }

  async function markAs(status: ItemStatus) {
    setBusy(true);
    setError(null);
    try {
      const { item: saved } = await api.items.update(id, { status });
      setItem(saved);
      toast(`Marked as ${STATUS_LABELS[status].toLowerCase()}.`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
    } finally {
      setBusy(false);
    }
  }

  const back = { label: "Item", onClick: () => onItem(id) };

  if (state === "loading") return <PageHeader title="Listing" back={back} />;
  if (state === "missing" || !item || !listing) {
    return (
      <>
        <PageHeader title="Not found" back={back} />
        <EmptyState icon="box" title="That item is not here">
          It was deleted, or it belongs to another inventory.
        </EmptyState>
      </>
    );
  }

  const withheld = withheldCount(item);

  return (
    <>
      <PageHeader
        back={back}
        title={item.title || <span className="untitled">Untitled item</span>}
        meta={<span className={`pill pill-status-${item.status}`}>{STATUS_LABELS[item.status]}</span>}
        actions={
          <button type="button" className="button" onClick={() => void copy("Listing", listing.text)}>
            <Icon name="copy" size={18} />
            Copy all
          </button>
        }
      />

      <div className="listing-layout">
        <Panel className="listing-main">
          <Segmented<ListingFormat>
            label="Where you are posting"
            value={format}
            onChange={choose}
            options={LISTING_FORMATS.map((option) => ({
              value: option,
              label: LISTING_FORMAT_LABELS[option],
            }))}
          />

          <p className="privacy-line">
            <Icon name="lock" size={14} />
            {withheld > 0
              ? `Public details only. ${plural(withheld, "private detail")} and the location are left out.`
              : "Public details only. The location is left out."}
          </p>

          {listing.warnings.length > 0 && (
            <ul className="warnings">
              {listing.warnings.map((warning) => (
                <li key={warning}>
                  <Notice tone="warn">{warning}</Notice>
                </li>
              ))}
            </ul>
          )}

          {copyProblem && <Notice tone="error">{copyProblem}</Notice>}

          {listing.parts.map((part) => {
            const over = part.limit !== null && part.value.length > part.limit;
            return (
              <div key={part.key} className="copy-field">
                <div className="copy-field-head">
                  <span className="field-label">{part.label}</span>
                  {part.limit !== null && (
                    <span className={over ? "count-text count-over" : "count-text"}>
                      {part.value.length} / {part.limit}
                    </span>
                  )}
                  <button
                    type="button"
                    className="button button-ghost button-small"
                    disabled={!part.value}
                    onClick={() => void copy(part.label, part.value)}
                  >
                    <Icon name="copy" size={14} />
                    Copy
                  </button>
                </div>
                <textarea
                  className="input input-area mono"
                  rows={part.key === "description" ? 7 : 1}
                  readOnly
                  aria-label={part.label}
                  value={part.value}
                  placeholder="Nothing to put here yet"
                />
              </div>
            );
          })}
        </Panel>

        <div className="listing-side">
          <Panel
            title="Photos"
            actions={
              item.photos.length > 1 ? (
                <button
                  type="button"
                  className="button button-ghost button-small"
                  disabled={busy}
                  onClick={() => void downloadAll()}
                >
                  <Icon name="download" size={14} />
                  {busy ? "Preparing…" : "All as zip"}
                </button>
              ) : undefined
            }
          >
            {item.photos.length === 0 ? (
              <p className="muted">No photos on this one.</p>
            ) : (
              <ul className="photo-downloads">
                {item.photos.map((photo, index) => (
                  <li key={photo.id}>
                    <a
                      href={photoPath(photo.id)}
                      download={`${slug(item.title)}-${index + 1}.jpg`}
                      aria-label={`Download photo ${index + 1}`}
                    >
                      <img src={thumbPath(photo.id)} alt="" loading="lazy" />
                      <span className="photo-download-icon">
                        <Icon name="download" size={16} />
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Status">
            {error && <Notice tone="error">{error}</Notice>}
            <div className="stack">
              <button
                type="button"
                className="button button-block"
                disabled={busy || item.status === "for_sale"}
                onClick={() => void markAs("for_sale")}
              >
                <Icon name="tag" size={18} />
                {item.status === "for_sale" ? "Listed for sale" : "Mark as for sale"}
              </button>
              <button
                type="button"
                className="button button-quiet button-block"
                disabled={busy || item.status === "sold"}
                onClick={() => void markAs("sold")}
              >
                <Icon name="check" size={18} />
                {item.status === "sold" ? "Sold" : "Mark as sold"}
              </button>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
